import jsdocTypes from '../../../../../jsdoc-types'; // eslint-disable-line no-unused-vars
import analytics from '../../../../../config/analytics';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import { filter } from '../../common/utils/objectUtils';
import { isEmpty } from '../../common/utils/type';

/**
 * @mixin analytics
 * @description
 * Defines a single `analytics` prop for contextual tracking data
 * (e.g., location, position, search term) and helper methods
 * to publish standardized analytics events.
 */

const analyticsEventBus = getAnalyticsEventBus();

/**
 * @enum {string}
 */
const Location = analytics.PRODUCT_TILE_LOCATION; // eslint-disable-line no-unused-vars

export default {
  /**
   * @property {Object} props
   * @property {Object} props.analytics
   * @property {Location} [props.analytics.location]
   *  Name of the product list or category where the item appears.
   * @property {number|string} [props.analytics.position]
   *  Position or index of the item within the list.
   * @property {string} [props.analytics.searchedTerm]
   *  Search term that produced this result, if applicable.
   */
  props: {
    analytics: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      analyticsConst: Object.freeze({
        location: analytics.PRODUCT_TILE_LOCATION,
      }),
    };
  },

  methods: {
    /**
     * @function analyticsExtendProp
     * @description
     * Merge the current analytics context with extra fields. Useful to
     * pass analytics props to child components.
     * @param {Object} [extension={}] Extra analytics fields.
     * @param {Object} [options] Options.
     * @param {boolean} [options.allowResetValues=false] Allow empty values
     *  (undefined, null, '') to reset existing analytics fields.
     * @returns {Object} Merged analytics object.
     * @example
     * const child = this.analyticsExtendProp({ position: 3 })
     * const child = this.analyticsExtendProp(
     *  { position: undefined },
     *  { allowResetValues: true }
     * )
     */
    analyticsExtendProp(extension = {}, { allowResetValues = false } = {}) {
      const filteredExtension = allowResetValues
        ? extension
        : filter(extension, (key, value) => !isEmpty(value));

      return { ...this.analytics, ...filteredExtension };
    },

    /**
     * @function analyticsPublishProductClickedEvent
     * @description
     * Publish an event when a user clicks a product tile.
     * @param {Object} product - Product data.
     * @param {Object} [extra={}] - Extra analytics data.
     * @example
     * this.analyticsPublishProductClickedEvent(product)
     */
    analyticsPublishProductClickedEvent(product, extra = {}) {
      analyticsEventBus.publish(analyticsEventBus.EVENTS.productTileClicked, {
        ...this.analytics,
        location: this.analytics.location,
        position: this.analytics.position,
        product,
        searched_term: this.analytics.searchedTerm,
        ...extra,
      });
    },

    /**
     * @function analyticsPublishProductViewedEvent
     * @description
     * Publish an event when a product tile is viewed.
     * @param {jsdocTypes.FullProduct} product - Product data.
     * @param {Object} [extra={}] - Extra analytics data.
     * @example
     * this.analyticsPublishProductViewedEvent(product)
     */
    analyticsPublishProductViewedEvent(product, extra = {}) {
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productImpressionViewed,
        {
          ...this.analytics,
          location: this.analytics.location,
          position: this.analytics.position,
          product,
          searched_term: this.analytics.searchedTerm,
          ...extra,
        }
      );
    },
  },
};
